import { useOnClickOutside } from '@hooks/useOnClickOutside'
import { TimeWidget } from 'components/form/time-widget'
import { FC, ReactNode, useCallback, useRef, useState } from 'react'
import { GridCell } from './GridCell'
import { Display } from './Display'
import { useSettings } from '@hooks/useSettings'
import { useInhibitKeyPressOverride } from '@hooks/useInhibitKeyPressOverride'
import { MealCheckbox } from './MealCheckbox'
import { DateTime } from 'luxon'
import { twMerge } from 'tailwind-merge'

interface TimeWidgetContainerProps {
  children: ReactNode
  hidden: boolean
  onClose: () => void
}

const TimeWidgetContainer: FC<TimeWidgetContainerProps> = ({
  children,
  hidden,
  onClose,
}) => {
  const ref = useRef(null)
  useOnClickOutside(ref, onClose)

  if (hidden) return null

  return (
    <div
      className={[
        'absolute right-0 top-7 w-[360px] drop-shadow-lg z-20',
        'will-change-[filter]', // Fix for shadow persisting after element is removed from DOM (hidden) on Safari
      ].join(' ')}
      ref={ref}
    >
      {children}
    </div>
  )
}

const getInitialIsMeal = (
  obj: Pick<BreakVariantProps, 'isMeal' | 'isMealByDefault'> | object,
) => {
  if (!('isMeal' in obj)) return false

  return obj.isMeal || obj.isMealByDefault
}

type BaseProps = {
  autoFocusedTimeWidgetAdjuster: 'start' | 'end'
  editable: boolean
  endAtSecondsSinceMidnight: number | null
  fieldPrefix?: string
  formattedContents: string
  isError?: boolean
  nextDay?: boolean
  onHoverLeave?: () => void
  onHoverOver?: () => void
  rowIndex: number
  startAtSecondsSinceMidnight: number | null
  testId?: string
  timeZone: string
  maxEndTime?: DateTime
  date?: DateTime
  timeWidgetTitle: string
} & (
    | { dateAgnostic: true; day: DayOfWeek }
    | { date: DateTime; minTime: DateTime; maxTime: DateTime }
  )

type TimeCardVariantProps = BaseProps & {
  onSubmit: (
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
  ) => void
  variant: 'time-card'
}

type BreakVariantProps = BaseProps & {
  isMeal: boolean
  isMealByDefault: boolean
  onSubmit: (
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
    isMeal: boolean,
  ) => void
  showIsMealCheckbox: boolean
  variant: 'break'
}

export type WorkDayCellProps = TimeCardVariantProps | BreakVariantProps

const timeWidgetVariantFromVariant = (variant: WorkDayCellProps['variant']) => {
  switch (variant) {
    case 'time-card':
      return 'time'
    case 'break':
      return 'time-and-duration'
  }
}

export const WorkDayCell: FC<WorkDayCellProps> = ({
  autoFocusedTimeWidgetAdjuster,
  editable,
  endAtSecondsSinceMidnight,
  fieldPrefix,
  formattedContents,
  isError,
  nextDay = false,
  onHoverOver,
  onHoverLeave,
  onSubmit,
  rowIndex,
  startAtSecondsSinceMidnight,
  testId,
  timeZone,
  variant,
  timeWidgetTitle,
  ...props
}) => {
  // The TimeWidget requires a concrete date for seconds from midnight calculations,
  // so we use an arbitrary date in date agnostic contexts. The date is largely irrelevant,
  // but it must not be on a DST boundary, otherwise seconds from midnight will not align
  // with the standard day and will not match the caller's expectations.
  // Use an explicit timezone so min/max time calculations are consistent regardless of
  // the local machine's timezone (the timeZone prop on this component is always Pacific).
  const defaultDate = DateTime.fromObject(
    { year: 2025, month: 8, day: 24 },
    { zone: timeZone },
  )
  // Assume midnight -> midnight in date agnostic contexts
  const defaultMinTime = defaultDate.startOf('day')
  const defaultMaxTime = defaultDate.plus({ days: 1 }).startOf('day')

  const { date, minTime, maxTime } =
    'dateAgnostic' in props
      ? {
        date: defaultDate,
        minTime: defaultMinTime,
        maxTime: defaultMaxTime,
      }
      : props

  const [timeWidgetHidden, setTimeWidgetHidden] = useState(true)
  const useSettingsQueryResult = useSettings()
  const { registerMenuClosed, registerMenuOpened } =
    useInhibitKeyPressOverride()
  const isInitiallyMeal = getInitialIsMeal(props)
  const [isMeal, setIsMeal] = useState(isInitiallyMeal)

  const close = useCallback(() => {
    registerMenuClosed('time-widget')
    setTimeWidgetHidden(true)
  }, [setTimeWidgetHidden, registerMenuClosed])

  const open = () => {
    registerMenuOpened('time-widget')
    setTimeWidgetHidden(false)
  }

  const gridCellPlacement =
    'dateAgnostic' in props ? { day: props.day } : { date }

  return (
    <GridCell
      {...gridCellPlacement}
      editable={editable}
      isError={isError}
      rowIndex={rowIndex}
      onMouseMove={onHoverOver}
      onMouseLeave={onHoverLeave}
      onClick={() => editable && open()}
      className={twMerge('relative', editable && 'cursor-pointer')}
      testId={testId}
    >
      <TimeWidgetContainer
        hidden={timeWidgetHidden}
        onClose={() => {
          close()
          setIsMeal(isInitiallyMeal)
        }}
      >
        <TimeWidget
          autoFocusedAdjuster={autoFocusedTimeWidgetAdjuster}
          dateEditing={date}
          displayMode={
            useSettingsQueryResult.data?.timeFormat === '24_hour' ? '24' : '12'
          }
          endAtSecondsSinceMidnight={endAtSecondsSinceMidnight}
          fieldPrefix={fieldPrefix}
          onSubmit={(
            startAtSecondsSinceMidnight,
            endAtSecondsSinceMidnight,
          ) => {
            switch (variant) {
              case 'time-card':
                onSubmit(startAtSecondsSinceMidnight, endAtSecondsSinceMidnight)
                break
              case 'break':
                onSubmit(
                  startAtSecondsSinceMidnight,
                  endAtSecondsSinceMidnight,
                  isMeal,
                )
                break
            }

            close()
          }}
          renderTopRight={() => {
            if (variant !== 'break') return null
            if (!('showIsMealCheckbox' in props)) return null
            if (!props.showIsMealCheckbox) return null

            return <MealCheckbox isMeal={isMeal} onChange={setIsMeal} />
          }}
          startAtSecondsSinceMidnight={startAtSecondsSinceMidnight}
          title={timeWidgetTitle}
          timeZone={timeZone}
          variant={timeWidgetVariantFromVariant(variant)}
          minTime={minTime}
          maxTime={maxTime}
        />
      </TimeWidgetContainer>
      <Display contents={formattedContents} nextDay={nextDay} />
    </GridCell>
  )
}
